import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ALL_COLUMNS, COLUMN_BY_ID, COLUMN_GROUPS } from './adColumns';
import useColumnPreferences from './useColumnPreferences';

type Prefs = ReturnType<typeof useColumnPreferences>;

// Sentinel value for the preset Select when the layout has unsaved edits.
const CUSTOM = '__custom__';

/** "Columns" button → popover for toggling, reordering, and saving column
 *  presets. All state lives in useColumnPreferences (localStorage-backed), so
 *  changes here never trigger a network request. */
export default function ColumnPicker({ prefs }: { prefs: Prefs }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [savingName, setSavingName] = useState<string | null>(null); // null = not in save mode

  const {
    visibleIds, activePreset, presets,
    toggleColumn, reorder, applyPreset, savePreset, deletePreset,
  } = prefs;

  const visibleSet = new Set(visibleIds);
  const hiddenColumns = ALL_COLUMNS.filter(c => !visibleSet.has(c.id));
  const isBuiltInActive = !!presets.find(p => p.name === activePreset)?.builtIn;

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    reorder(result.source.index, result.destination.index);
  };

  const commitSave = () => {
    if (savingName && savingName.trim()) {
      savePreset(savingName.trim());
      setSavingName(null);
    }
  };

  const close = () => { setAnchorEl(null); setSavingName(null); };

  return (
    <>
      <Button size="small" variant="outlined" startIcon={<ViewColumnIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}>
        Columns
      </Button>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 340, maxHeight: 580, p: 1.5 } }}
      >
        {/* ── Presets ── */}
        <Typography variant="overline" color="text.secondary">Preset</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 1 }}>
          <FormControl size="small" fullWidth>
            <Select
              value={activePreset || CUSTOM}
              onChange={(e) => { const v = e.target.value as string; if (v !== CUSTOM) applyPreset(v); }}
            >
              {!activePreset && <MenuItem value={CUSTOM}>Custom (unsaved)</MenuItem>}
              {presets.map(p => <MenuItem key={p.name} value={p.name}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
          {activePreset && !isBuiltInActive && (
            <IconButton size="small" title="Delete preset" onClick={() => deletePreset(activePreset)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        {savingName == null ? (
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
            <Button size="small" onClick={() => setSavingName('')}>Save as preset…</Button>
            {activePreset && !isBuiltInActive && (
              <Button size="small" onClick={() => savePreset(activePreset)}>Update “{activePreset}”</Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
            <TextField size="small" autoFocus fullWidth placeholder="Preset name" value={savingName}
              onChange={(e) => setSavingName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') commitSave(); }} />
            <Button size="small" variant="contained" onClick={commitSave} disabled={!savingName.trim()}>Save</Button>
            <Button size="small" onClick={() => setSavingName(null)}>Cancel</Button>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        {/* ── Visible (drag to reorder) ── */}
        <Typography variant="overline" color="text.secondary">Visible — drag to reorder</Typography>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="visibleColumns">
            {(provided) => (
              <Box {...provided.droppableProps} ref={provided.innerRef} sx={{ mb: 0.5 }}>
                {visibleIds.map((id, index) => {
                  const col = COLUMN_BY_ID[id];
                  if (!col) return null;
                  return (
                    <Draggable key={id} draggableId={id} index={index}>
                      {(p) => (
                        <Box ref={p.innerRef} {...p.draggableProps}
                          sx={{ display: 'flex', alignItems: 'center', bgcolor: 'background.paper' }}>
                          <Box {...p.dragHandleProps} sx={{ display: 'flex', color: 'text.disabled', cursor: 'grab' }}>
                            <DragIndicatorIcon fontSize="small" />
                          </Box>
                          <Checkbox size="small" checked onChange={() => toggleColumn(id)} />
                          <Typography variant="body2">{col.label}</Typography>
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
        {visibleIds.length === 0 && (
          <Typography variant="caption" color="text.secondary">No columns selected.</Typography>
        )}

        <Divider sx={{ my: 1 }} />

        {/* ── Available (grouped) ── */}
        <Typography variant="overline" color="text.secondary">Add columns</Typography>
        {COLUMN_GROUPS.map(group => {
          const groupCols = hiddenColumns.filter(c => c.group === group);
          if (groupCols.length === 0) return null;
          return (
            <Box key={group} sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mt: 0.5 }}>
                {group}
              </Typography>
              {groupCols.map(col => (
                <Box key={col.id} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox size="small" checked={false} onChange={() => toggleColumn(col.id)} />
                  <Typography variant="body2">{col.label}</Typography>
                </Box>
              ))}
            </Box>
          );
        })}
        {hiddenColumns.length === 0 && (
          <Typography variant="caption" color="text.secondary">All columns are visible.</Typography>
        )}
      </Popover>
    </>
  );
}
